import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  SMS_AI_ERROR_MESSAGE,
  SMS_AI_HELPER_TEXT,
  SMS_AI_PROMPT_PLACEHOLDER,
  SMS_AI_TONE_OPTIONS,
  buildWriteSmsPayload,
  canGenerateSms,
  canUseGeneratedSms,
  generateSmsFallback,
} from '../../utils/writeSmsAIUtils';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const WriteSmsWithAiModal = ({
  isOpen,
  onClose,
  onUseMessage,
  workflowId,
  stepId,
  existingMessage = '',
  token,
}) => {
  const [tone, setTone] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const resetState = useCallback(() => {
    setTone('');
    setPrompt('');
    setGeneratedMessage('');
    setIsGenerating(false);
    setError('');
  }, []);

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  const handleGenerate = async () => {
    if (!canGenerateSms(prompt)) return;

    setIsGenerating(true);
    setError('');
    setGeneratedMessage('');

    const payload = buildWriteSmsPayload({
      tone: tone || 'Friendly',
      prompt,
      workflowId,
      stepId,
      existingMessage,
    });

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/automations/ai/write-sms`, payload, { headers });
      const message = response.data?.message || response.data?.sms || '';
      if (message.trim()) {
        setGeneratedMessage(message.trim());
        return;
      }
      setGeneratedMessage(generateSmsFallback(tone || 'Friendly', prompt));
    } catch {
      setGeneratedMessage(generateSmsFallback(tone || 'Friendly', prompt));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseMessage = () => {
    if (!canUseGeneratedSms(generatedMessage)) return;
    onUseMessage?.(generatedMessage);
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        priority
        className="max-w-2xl p-0 gap-0 overflow-hidden"
        data-testid="write-sms-ai-modal"
        data-workflow-editor-ignore-pan
      >
        <div className="p-6 pb-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-xl font-semibold text-slate-900">
              Write SMS with AI
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed">
              Use AI to generate the SMS. Simply describe what you need, and AI will write the SMS for you.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ai-sms-tone" className="text-sm font-semibold text-slate-800">
                Tone
              </Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger
                  id="ai-sms-tone"
                  data-testid="ai-sms-tone-select"
                  className="h-11 w-[430px] max-w-full bg-white border-slate-200 focus:ring-2 focus:ring-blue-500/20"
                >
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent className="z-[220] bg-white border border-slate-200 shadow-lg rounded-lg">
                  {SMS_AI_TONE_OPTIONS.map((option) => (
                    <SelectItem
                      key={option}
                      value={option}
                      data-testid={`ai-sms-tone-${option.toLowerCase()}`}
                      className="cursor-pointer"
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-sms-prompt" className="text-sm font-semibold text-slate-800">
                Type your prompt
              </Label>
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20">
                <Textarea
                  id="ai-sms-prompt"
                  data-testid="ai-sms-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={SMS_AI_PROMPT_PLACEHOLDER}
                  className="min-h-[140px] border-0 rounded-none resize-y shadow-none focus-visible:ring-0 text-sm"
                />
                <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-3 py-2.5 bg-white">
                  <p className="flex items-start gap-1.5 text-xs text-slate-500 leading-snug max-w-[65%]">
                    <Sparkles size={14} className="mt-0.5 shrink-0 text-slate-400" />
                    {SMS_AI_HELPER_TEXT}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="ai-sms-generate-button"
                    disabled={!canGenerateSms(prompt) || isGenerating}
                    onClick={handleGenerate}
                    className={cn(
                      'shrink-0 border-purple-500 text-purple-600 hover:bg-purple-50 hover:text-purple-700',
                      'disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent',
                    )}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} className="mr-1.5" />
                        Generate SMS
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {error ? (
              <p className="text-sm text-red-600" data-testid="ai-sms-error">{error}</p>
            ) : null}

            {generatedMessage ? (
              <div className="space-y-2" data-testid="ai-sms-preview">
                <Label className="text-sm font-semibold text-slate-800">Generated SMS</Label>
                <Textarea
                  data-testid="ai-sms-generated-text"
                  value={generatedMessage}
                  onChange={(e) => setGeneratedMessage(e.target.value)}
                  className="min-h-[100px] bg-slate-50"
                />
                <p className="text-xs text-slate-500" data-testid="ai-sms-char-count">
                  {generatedMessage.length} characters
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 bg-white">
          <Button
            type="button"
            variant="ghost"
            data-testid="ai-sms-cancel-button"
            onClick={onClose}
            className="text-slate-600"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="ai-sms-use-button"
            disabled={!canUseGeneratedSms(generatedMessage)}
            onClick={handleUseMessage}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
          >
            Use message
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WriteSmsWithAiModal;
